import { Controller } from "@hotwired/stimulus"

/**
 * Inline Edit Controller
 *
 * Click-to-edit for a single attribute, driven by the components/editable_field
 * partial: a pencil opens an input with explicit Save / Cancel, Enter saves and
 * Escape reverts. Saving PATCHes the record and lets the turbo-stream reply
 * refresh anything else on the page that echoes the value.
 *
 * Values:
 *   - url: where to PATCH
 *   - field: the attribute being edited
 *   - scope: the param scope the field nests under (defaults to "user")
 */
export default class extends Controller {
  static targets = ["display", "editor", "input", "value", "error", "trigger", "submit"]
  static values = {
    url: String,
    field: String,
    scope: { type: String, default: "user" }
  }

  connect() {
    this.editing = false
    this.savedValue = this.currentValue()
  }

  edit() {
    if (this.editing) return

    this.editing = true
    this.clearError()
    this.displayTarget.classList.add("d-none")
    this.editor.classList.remove("d-none")
    this.element.classList.add("is-editing")
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  cancel() {
    this.inputTarget.value = this.savedValue
    this.close()
  }

  close() {
    this.editing = false
    this.clearError()
    this.editor.classList.add("d-none")
    this.displayTarget.classList.remove("d-none")
    this.element.classList.remove("is-editing")
    if (this.hasTriggerTarget) this.triggerTarget.focus()
  }

  // A field with Save / Cancel wraps its input in an editor; a bare cell editor (the
  // payments table) shows and hides the input itself.
  get editor() {
    return this.hasEditorTarget ? this.editorTarget : this.inputTarget
  }

  async save() {
    if (!this.hasUrlValue || !this.hasFieldValue) return

    const value = this.inputTarget.value.trim()

    if (value === this.savedValue) {
      this.close()
      return
    }

    const formData = new FormData()
    formData.append(`${this.scopeValue}[${this.fieldValue}]`, value)
    formData.append("_method", "PATCH")

    this.setBusy(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "Accept": "text/vnd.turbo-stream.html"
        },
        credentials: "same-origin"
      })

      const body = await response.text()

      if (!response.ok) {
        this.showError(this.errorMessageFrom(body) || "Could not save. Please try again.")
        return
      }

      this.savedValue = value
      this.close()
      // The stream replaces this field along with the header and breadcrumb, so
      // render it after closing the editor rather than patching the DOM by hand.
      Turbo.renderStreamMessage(body)
    } catch (error) {
      console.error(`Error updating ${this.fieldValue}:`, error)
      this.showError("Something went wrong. Please try again.")
    } finally {
      this.setBusy(false)
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.save()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    }
  }

  currentValue() {
    return this.hasInputTarget ? this.inputTarget.value.trim() : ""
  }

  // A rejected save answers with the validation messages rather than a stream,
  // so the field can explain itself instead of firing a browser alert.
  errorMessageFrom(body) {
    try {
      const data = JSON.parse(body)
      if (data?.errors?.length) return data.errors.join(", ")
    } catch {
      // Not a JSON payload — fall back to the generic message.
    }
    return null
  }

  setBusy(busy) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = busy
    this.inputTarget.disabled = busy
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  clearError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }
}
